<template>
  <div>
    <Navbar />

    <!-- Hero Section -->
    <section
      class="relative flex items-center bg-center bg-no-repeat bg-cover min-h-[70vh] lg:min-h-[75vh]"
      :style="{
        paddingTop: '8vw',
        paddingBottom: '8vw',
        backgroundImage: `url(${require('@/assets/images/brand-pattern.png')})`,
        backgroundSize: '900px',
        backgroundRepeat: 'repeat',
        backgroundPosition: 'center',
      }"
    >
      <div class="container relative z-10">
        <div class="grid items-center grid-cols-1 gap-10 md:grid-cols-2">
          <!-- Text Content -->
          <div class="space-y-5">
            <h1
              class="text-3xl md:text-4xl lg:text-5xl 2xl:text-[50px] font-bold leading-tight text-primary"
            >
              {{ t("about_title") }}
            </h1>

            <h2
              class="text-lg font-medium leading-relaxed md:text-xl lg:text-2xl text-slate-700 dark:text-slate-300"
            >
              {{ t("slogan") }}
              <span class="font-semibold text-secondary">{{
                t("vision_2030")
              }}</span>
            </h2>

            <p class="max-w-2xl text-lg text-slate-700 dark:text-slate-300">
              {{ t("about_paragraph") }}
            </p>

            <div class="flex flex-wrap items-center gap-3 pt-4">
              <a
                href="/registerClient"
                class="inline-flex items-center justify-center font-semibold tracking-wide text-white transition-all duration-300 rounded-md shadow-md h-11 px-7 bg-primary hover:bg-primary-light"
              >
                <i class="text-xl mdi mdi-briefcase-outline me-2"></i>
                {{ t("start_now") }}
              </a>

              <a
                href="/contact"
                class="inline-flex items-center justify-center font-semibold tracking-wide text-white transition-all duration-300 rounded-md shadow-md h-11 px-7 bg-secondary hover:bg-secondary-dark"
              >
                <i class="text-xl mdi mdi-chat-outline me-2"></i>
                {{ t("contact_us") }}
              </a>
            </div>
          </div>

          <!-- Image / Side Visual -->
          <div class="relative">
            <img
              src="../assets/images/about.jpg"
              class="mx-auto rounded-[120px] rounded-br-2xl shadow-lg dark:shadow-gray-800 w-[75%] h-[600px] object-cover"
              alt="Consulting Team"
            />

            <!-- Visitor Card (Consultations) -->
            <div
              class="absolute bottom-0 flex items-center justify-between p-4 mb-20 border rounded-lg shadow-md right-3 bg-white/70 dark:bg-slate-900/80 w-60 backdrop-blur-md border-primary/20"
            >
              <div class="flex items-center">
                <div
                  class="flex items-center justify-center h-[65px] min-w-[65px] bg-gradient-to-tr from-primary/10 to-secondary/30 text-primary rounded-full me-3"
                >
                  <i class="text-3xl mdi mdi-monitor"></i>
                </div>

                <div>
                  <span
                    class="text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    {{ t("stats_consulting") }}
                  </span>

                  <p class="text-xl font-bold text-primary">
                    {{ stats.consultations_count }}
                  </p>

                  <!-- Optional: show last30/prev30 tiny hint (remove if you don't want it) -->
                  <!-- <p class="mt-0.5 text-[11px] text-slate-600 dark:text-slate-400"> -->
                    <!-- {{ stats.consultations_last30 ?? 0 }} / {{ stats.consultations_prev30 ?? 0 }}
                 {{ t("last30_vs_prev30") }}  -->
                  <!-- </p> -->
                </div>
              </div>

              <span
                class="flex items-center gap-1 text-sm font-medium"
                :class="consultationsGrowthUI.className"
                :title="consultationsGrowthUI.tooltip"
              >
                <i class="mdi" :class="consultationsGrowthUI.icon"></i>

                <!-- ✅ If percent exists: show +12% -->
                <template v-if="consultationsGrowthUI.showPercent">
                  {{ consultationsGrowthUI.text }}{{ t("percent_sign") }}
                </template>

                <!-- ✅ Else: show label + delta e.g. "طفرة (+23)" -->
                <template v-else>
                  {{ consultationsGrowthUI.text }}
                </template>
              </span>
            </div>

            <!-- Clients Card -->
            <div
              class="absolute flex flex-col justify-between p-4 border rounded-lg shadow-md left-5 top-12 bg-white/70 dark:bg-slate-900/80 w-52 backdrop-blur-md border-primary/20 text-primary"
            >
              <h5
                class="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200"
              >
                {{ t("stats_clients") }}
              </h5>

              <div
                class="flex justify-between mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
              >
                <span>{{ t("progress") }}</span>
                <span
                  >{{ stats.clients_progress_percent
                  }}{{ t("percent_sign") }}</span
                >
              </div>

              <div
                class="w-full bg-slate-300/40 dark:bg-slate-700/50 rounded-full h-[6px] overflow-hidden"
              >
                <div
                  class="bg-gradient-to-r from-primary to-secondary h-[6px] rounded-full transition-all duration-700 ease-in-out"
                  :style="{ width: stats.clients_progress_percent + '%' }"
                ></div>
              </div>
            </div>

            <!-- (Optional) tiny loading indicator (won't change layout) -->
            <div
              v-if="loading"
              class="absolute -bottom-10 right-3 text-xs text-slate-500 dark:text-slate-400"
            >
              {{ t("stats_updating") }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <Features />
    <About />
    <Values />
    <Pricing />
  </div>

  <Footer />
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { messages } from "@/i18n";

import Navbar from "@/components/Navbar.vue";
import About from "@/components/About.vue";
import Features from "@/components/Features.vue";
import Values from "@/components/Values.vue";
import Pricing from "@/components/Pricing.vue";
import Footer from "@/components/Footer.vue";

import { useHomeStatsStore } from "@/stores/homeStatsStore";

const store = useHomeStatsStore();

// language
const lang = ref(
  (document.documentElement.lang || localStorage.getItem("app_lang") || "ar")
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar",
);

let _langObserver = null;

function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

const stats = computed(() => store.stats || {});
const loading = computed(() => store.loading);

// ✅ Map backend labels to UI text (AR/EN)
function growthLabelText(label) {
  const ar = {
    NEW: "جديد",
    NO_CHANGE: "بدون تغيير",
    NORMAL: "نمو",
    SPIKE_SMALL_BASE: "طفرة",
    DROP_SMALL_BASE: "هبوط",
    FLAT_SMALL_BASE: "ثبات",
  };

  const en = {
    NEW: "New",
    NO_CHANGE: "No change",
    NORMAL: "Growth",
    SPIKE_SMALL_BASE: "Spike",
    DROP_SMALL_BASE: "Drop",
    FLAT_SMALL_BASE: "Flat",
  };

  const dict = lang.value === "en" ? en : ar;
  return dict[label] || (lang.value === "en" ? "Growth" : "نمو");
}

// ✅ Consultations Growth UI (handles percent=null)
const consultationsGrowthUI = computed(() => {
  const percentRaw = stats.value.consultations_growth_percent;
  const hasPercent = percentRaw !== null && percentRaw !== undefined && percentRaw !== "";
  const percent = hasPercent ? Number(percentRaw) : null;

  const label = String(stats.value.consultations_growth_label || "NORMAL");
  const delta = Number(stats.value.consultations_growth_delta ?? 0);

  // Decide UI color + icon
  const isNegative = hasPercent ? percent < 0 : delta < 0;
  const isPositive = hasPercent ? percent > 0 : delta > 0;

  const className = isNegative
    ? "text-red-600 dark:text-red-400"
    : "text-green-600 dark:text-green-400";

  const icon = isNegative ? "mdi-trending-down" : "mdi-trending-up";

  // Build text
  let text = "";
  let tooltip = "";

  if (hasPercent) {
    // show signed percent
    text = (percent > 0 ? "+" : "") + percent;
    tooltip =
      (lang.value === "en" ? "Growth %" : "نسبة النمو") + `: ${text}%`;
    return { text, showPercent: true, className, icon, tooltip };
  }

  // No percent: show label + delta (small base / new)
  const labelText = growthLabelText(label);

  // show delta with sign
  const deltaText = (delta > 0 ? "+" : "") + delta;

  // Example: "طفرة (+23)" or "New (+23)"
  // If delta = 0, just show label
  text = delta !== 0 ? `${labelText} (${deltaText})` : labelText;

  const last30 = Number(stats.value.consultations_last30 ?? 0);
  const prev30 = Number(stats.value.consultations_prev30 ?? 0);

  tooltip =
    (lang.value === "en"
      ? `Last 30: ${last30} | Prev 30: ${prev30}`
      : `آخر 30 يوم: ${last30} | الـ 30 السابقة: ${prev30}`);

  return { text, showPercent: false, className, icon, tooltip };
});

onMounted(async () => {
  // observe lang changes
  _langObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "attributes" && m.attributeName === "lang") {
        const htmlLang = document.documentElement.lang || "ar";
        lang.value = htmlLang.toLowerCase().startsWith("en") ? "en" : "ar";
      }
    }
  });
  _langObserver.observe(document.documentElement, { attributes: true });

  // load stats
  await store.getStats({ force: false, ttlMs: 60_000 });
});

onBeforeUnmount(() => {
  if (_langObserver) _langObserver.disconnect();
});
</script>